import { useEffect, useState } from 'react';
import { Plus, Edit, UserX, UserCheck, Dumbbell, MailCheck, Video } from 'lucide-react';
import { supabase } from '../services/supabase';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import type { Database } from '../types/database.types';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type MonitorProfileBrief = Pick<
  ProfileRow,
  | 'id'
  | 'full_name'
  | 'email'
  | 'phone'
  | 'active'
  | 'is_physical_trainer'
  | 'can_do_video_analysis'
  | 'created_at'
>;

interface Monitor {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  is_physical_trainer: boolean;
  can_do_video_analysis: boolean;
  created_at: string;
  /** `null` = no se ha podido contar. No es 0: un 0 dice que no lleva grupos. */
  grupos_count?: number | null;
}

interface MonitorForm {
  full_name: string;
  email: string;
  phone: string;
  is_physical_trainer: boolean;
  can_do_video_analysis: boolean;
}

const FORM_EMPTY: MonitorForm = {
  full_name: '',
  email: '',
  phone: '',
  is_physical_trainer: false,
  can_do_video_analysis: false,
};

export default function MonitoresPage() {
  const [monitores, setMonitores] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Monitor | null>(null);
  const [form, setForm] = useState<MonitorForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // `error` es del modal de invitación. El interruptor de la fila necesita el
  // suyo: sin él, desactivar a un entrenador se pintaba en pantalla aunque la
  // base lo rechazara.
  // `listError` es el hueco de las ESCRITURAS (activar/desactivar). La carga
  // tiene el suyo: compartirlo haría que un `setListError('')` de un toggle
  // borrase el aviso de la lectura.
  const [listError, setListError] = useState('');
  // «Sin entrenadores» es una afirmación sobre la plantilla del club.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  // Los grupos son otra lectura: sin ellos la columna «Grupos» sale a 0 para
  // todos, y un 0 ahí se lee como «este entrenador no lleva ningún grupo» —
  // que es justo el criterio con el que se decide a quién asignarle uno.
  const [errorGrupos, setErrorGrupos] = useState(false);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  const [confirmDesactivar, setConfirmDesactivar] = useState<Monitor | null>(null);

  useEffect(() => {
    loadMonitores();
  }, []);

  async function loadMonitores() {
    setLoading(true);
    const { data, error: errorLectura } = await supabase
      .from('profiles')
      .select(
        'id, full_name, email, phone, active, is_physical_trainer, can_do_video_analysis, created_at'
      )
      .eq('role', 'monitor')
      .order('full_name');

    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setMonitores([]);
      setLoading(false);
      return;
    }
    setErrorCarga(null);

    const monitores = (data ?? []) as MonitorProfileBrief[];
    const ids = monitores.map((m) => m.id);

    // Una sola query agregada: traer todos los `monitor_id` de grupos activos
    // de los monitores listados y contar en cliente. Sustituye el N+1 anterior
    // (1 query por monitor) por 2 queries fijas.
    let groupCounts: Record<string, number> = {};
    let gruposIlegibles = false;
    if (ids.length) {
      const { data: groupsData, error: errorLecturaGrupos } = await supabase
        .from('groups')
        .select('monitor_id')
        .eq('active', true)
        .in('monitor_id', ids);
      gruposIlegibles = !!errorLecturaGrupos;
      const rows = (groupsData ?? []) as { monitor_id: string | null }[];
      groupCounts = rows.reduce<Record<string, number>>((acc, g) => {
        if (g.monitor_id) acc[g.monitor_id] = (acc[g.monitor_id] ?? 0) + 1;
        return acc;
      }, {});
    }

    const withCounts: Monitor[] = monitores.map((m) => ({
      ...m,
      // `null` = sin contar, y la columna lo pinta «—». Un 0 diría que no
      // lleva grupos, que es el dato con el que se le asigna uno.
      grupos_count: gruposIlegibles ? null : (groupCounts[m.id] ?? 0),
    }));
    setErrorGrupos(gruposIlegibles);
    setMonitores(withCounts);
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_EMPTY);
    setError('');
    setModal(true);
  }

  function openEdit(m: Monitor) {
    setEditTarget(m);
    setForm({
      full_name: m.full_name,
      email: m.email ?? '',
      phone: m.phone ?? '',
      is_physical_trainer: m.is_physical_trainer,
      can_do_video_analysis: m.can_do_video_analysis,
    });
    setError('');
    setModal(true);
  }

  async function handleSave() {
    if (!form.full_name || !form.email) {
      setError('Nombre y email son obligatorios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editTarget) {
        // La rama de invitar (abajo) sí comprobaba su error y lo lanzaba al
        // catch. Esta no: pintaba el entrenador editado en la lista y cerraba
        // el modal aunque la base hubiera rechazado el UPDATE, y entre lo que
        // se veía cambiado estaba `can_do_video_analysis`, que es un permiso.
        const { error: updError } = await supabase
          .from('profiles')
          .update({
            full_name: form.full_name,
            phone: form.phone || null,
            is_physical_trainer: form.is_physical_trainer,
            can_do_video_analysis: form.can_do_video_analysis,
          })
          .eq('id', editTarget.id);
        if (updError) throw new Error(updError.message);
        setMonitores((prev) =>
          prev.map((m) =>
            m.id === editTarget.id
              ? {
                  ...m,
                  full_name: form.full_name,
                  phone: form.phone || null,
                  is_physical_trainer: form.is_physical_trainer,
                  can_do_video_analysis: form.can_do_video_analysis,
                }
              : m
          )
        );
        setModal(false);
      } else {
        const redirectTo = `${window.location.origin}/set-password`;
        const { data: fnData, error: fnErr } = await supabase.functions.invoke('create-monitor', {
          body: {
            email: form.email,
            full_name: form.full_name,
            phone: form.phone,
            redirect_to: redirectTo,
            is_physical_trainer: form.is_physical_trainer,
            can_do_video_analysis: form.can_do_video_analysis,
          },
        });
        if (fnErr) {
          let msg = 'Error al invitar al entrenador';
          try {
            // `FunctionsHttpError` (de @supabase/supabase-js) trae `context: Response`
            // con el body de la edge function; otros tipos de error no — guard estructural.
            const ctx = (fnErr as { context?: { json?: () => Promise<{ error?: string }> } })
              .context;
            const body = await ctx?.json?.();
            msg = body?.error ?? fnErr.message;
          } catch {
            msg = fnErr.message;
          }
          throw new Error(msg);
        }
        if (fnData?.error) throw new Error(fnData.error);
        await loadMonitores();
        setInviteSentTo(form.email);
        setModal(false);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActivo(m: Monitor) {
    const { error: toggleError } = await supabase
      .from('profiles')
      .update({ active: !m.active })
      .eq('id', m.id);
    // El error se comprueba ANTES de tocar el estado: pintar la fila con el
    // valor nuevo cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (toggleError) {
      setListError(
        `No se pudo ${m.active ? 'desactivar' : 'activar'} a ${m.full_name}: ${toggleError.message}`
      );
      return;
    }
    setListError('');
    setMonitores((prev) => prev.map((x) => (x.id === m.id ? { ...x, active: !x.active } : x)));
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={openCreate}
          className="btn-gold flex items-center gap-2"
          data-testid="monitores-add-btn"
        >
          <Plus size={16} /> Invitar Entrenador
        </button>
      </div>

      {errorGrupos && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="monitores-grupos-ilegibles"
        >
          No se han podido contar los grupos de cada entrenador. La columna «Grupos» está sin
          comprobar: no la lea como que nadie lleva ninguno.
        </p>
      )}

      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="monitores-list-error"
        >
          {listError}
        </p>
      )}

      {inviteSentTo && (
        <div
          className="flex items-start gap-3 bg-success/10 border border-success/30 rounded-lg p-4"
          data-testid="monitores-invite-sent"
        >
          <MailCheck size={18} className="text-success flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-success font-medium text-sm">Invitación enviada</p>
            <p className="text-text-secondary text-xs mt-1">
              Hemos enviado un email a <span className="text-white font-mono">{inviteSentTo}</span>{' '}
              con el enlace para definir su contraseña.
            </p>
          </div>
          <button
            onClick={() => setInviteSentTo(null)}
            className="text-text-secondary hover:text-white text-xs"
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th">Nombre</th>
                <th className="table-th">Email</th>
                <th className="table-th">Teléfono</th>
                <th className="table-th">Grupos activos</th>
                <th className="table-th">Rol PF</th>
                <th className="table-th">Estado</th>
                <th className="table-th">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {errorCarga ? (
                <tr>
                  <td colSpan={7} className="table-td text-center py-12">
                    <span className="text-error text-sm" data-testid="monitores-ilegibles">
                      No se ha podido cargar la plantilla de entrenadores. No es que no haya
                      ninguno: la consulta ha fallado.
                    </span>
                    <button
                      className="btn-secondary block mx-auto mt-3"
                      onClick={loadMonitores}
                      data-testid="monitores-reintentar"
                    >
                      Reintentar
                    </button>
                  </td>
                </tr>
              ) : monitores.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-td text-center text-text-secondary py-12">
                    Sin entrenadores
                  </td>
                </tr>
              ) : (
                monitores.map((m) => (
                  <tr key={m.id} className="table-row">
                    <td className="table-td font-medium">{m.full_name}</td>
                    <td className="table-td text-text-secondary">{m.email}</td>
                    <td className="table-td text-text-secondary">{m.phone ?? '—'}</td>
                    <td className="table-td">
                      <span className="badge-gold" data-testid={`monitor-grupos-${m.id}`}>
                        {m.grupos_count === null ? '—' : `${m.grupos_count ?? 0} grupos`}
                      </span>
                    </td>
                    <td className="table-td">
                      <div className="flex flex-col gap-1">
                        {m.is_physical_trainer && (
                          <span className="badge-success flex items-center gap-1 w-fit">
                            <Dumbbell size={11} /> Prep. Físico
                          </span>
                        )}
                        {m.can_do_video_analysis && (
                          <span className="badge-gold flex items-center gap-1 w-fit">
                            <Video size={11} /> Análisis vídeo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={m.active ? 'badge-success' : 'badge-error'}>
                        {m.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(m)}
                          className="text-text-secondary hover:text-gold-primary transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDesactivar(m)}
                          className="text-text-secondary hover:text-error transition-colors"
                        >
                          {m.active ? <UserX size={16} /> : <UserCheck size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal crear/editar */}
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar Entrenador' : 'Invitar Entrenador'}
      >
        <div className="space-y-4">
          {error && <p className="text-error text-sm bg-error/10 rounded-lg p-3">{error}</p>}
          {!editTarget && (
            <p className="text-text-secondary text-xs bg-bg-elevated rounded-lg p-3 border border-bg-border">
              Le enviaremos un email con un enlace para que defina su contraseña. No necesita una
              contraseña temporal — el monitor la elige en el primer acceso.
            </p>
          )}
          <div>
            <label className="label">Nombre completo *</label>
            <input
              className="input"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              data-testid="monitor-form-name"
            />
          </div>
          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={!!editTarget}
              data-testid="monitor-form-email"
            />
          </div>
          <div>
            <label className="label">Teléfono</label>
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="monitor-form-phone"
            />
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated border border-bg-border">
            <input
              type="checkbox"
              id="is_physical_trainer"
              checked={form.is_physical_trainer}
              onChange={(e) => setForm((f) => ({ ...f, is_physical_trainer: e.target.checked }))}
              className="w-4 h-4 accent-gold-primary"
            />
            <label htmlFor="is_physical_trainer" className="flex items-center gap-2 cursor-pointer">
              <Dumbbell size={16} className="text-green-400" />
              <div>
                <p className="text-white text-sm font-medium">
                  Preparador físico (módulo Preparación Física)
                </p>
                <p className="text-text-secondary text-xs">
                  Puede crear rutinas y revisar vídeos de alumnos con suscripción Preparación Física
                </p>
              </div>
            </label>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated border border-bg-border">
            <input
              type="checkbox"
              id="can_do_video_analysis"
              checked={form.can_do_video_analysis}
              onChange={(e) => setForm((f) => ({ ...f, can_do_video_analysis: e.target.checked }))}
              className="w-4 h-4 accent-gold-primary"
            />
            <label
              htmlFor="can_do_video_analysis"
              className="flex items-center gap-2 cursor-pointer"
            >
              <Video size={16} className="text-gold-primary" />
              <div>
                <p className="text-white text-sm font-medium">Análisis de vídeo</p>
                <p className="text-text-secondary text-xs">
                  Puede ser asignado como entrenador de análisis de vídeo y completar sesiones
                </p>
              </div>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold"
              data-testid="monitor-form-submit"
            >
              {saving
                ? editTarget
                  ? 'Guardando...'
                  : 'Enviando invitación...'
                : editTarget
                  ? 'Guardar cambios'
                  : 'Enviar invitación'}
            </button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={!!confirmDesactivar}
        onClose={() => setConfirmDesactivar(null)}
        onConfirm={() => confirmDesactivar && toggleActivo(confirmDesactivar)}
        title={confirmDesactivar?.active ? 'Desactivar entrenador' : 'Activar entrenador'}
        message={`¿Seguro que quieres ${confirmDesactivar?.active ? 'desactivar' : 'activar'} a ${confirmDesactivar?.full_name}?`}
        danger={confirmDesactivar?.active}
      />
    </div>
  );
}
